import { useState } from "react";
import { Link, Redirect, useLocalSearchParams } from "expo-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, FormTitle, InputWithLabel } from "@ptsd/uikit";
import { useQueryClient } from "@tanstack/react-query";
import i18next from "i18next";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Form, Separator, Text, useMedia, View, YStack } from "tamagui";
import z from "zod";

import { api } from "~/utils/api";
import { authClient } from "~/utils/auth";
import img from "~/assets/signup/signup-2.png";
import { FacebookButton } from "~/component/feature/auth/facebook-button";
import { GoogleButton } from "~/component/feature/auth/google-button";
import { OrSeparator } from "~/component/feature/auth/or-separator";
import {
  AuthBaseForm,
  AuthContainer,
  AuthContent,
  AuthImage,
} from "~/component/feature/auth/wrappers";
import { GlobalErrorScreen } from "~/component/global-error-screen";

export default function AuthPage() {
  const { t } = useTranslation("signin");
  const { error: globalError } = useLocalSearchParams<{ error: string }>();

  if (globalError === "unable_to_create_user") {
    return <GlobalErrorScreen error={t("error.userNotFound")} />;
  }

  return (
    <AuthContainer>
      <AuthContent>
        <SigninForm />
      </AuthContent>

      <AuthImage src={img} />
    </AuthContainer>
  );
}

export const schema = z.object({
  email: z
    .string()
    .nonempty(i18next.t("signup:validation.required"))
    .email(i18next.t("signup:validation.email.invalidFormat")),

  password: z
    .string()
    .nonempty(i18next.t("signup:validation.required"))
    .min(8, i18next.t("signup:validation.password.minLength"))
    .regex(
      /^(?=.*[A-Z])[A-Za-z0-9!@#$%^&*()_+{}[\]:;<>,.?/\\~-]{8,}$/,
      i18next.t("signup:validation.password.minLength"),
    ),
});

type SigninFormData = z.infer<typeof schema>;

const SigninForm = () => {
  const { t } = useTranslation("signin");
  const { sm } = useMedia();

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const apiUtils = api.useUtils();
  const queryClient = useQueryClient();

  const { setError, control, handleSubmit } = useForm<SigninFormData>({
    defaultValues: { email: "", password: "" },
    resolver: zodResolver(schema),
  });

  const onSubmit: SubmitHandler<SigninFormData> = (data) => {
    if (isLoading) return;

    void (async () => {
      setIsLoading(true);
      const result = await authClient.signIn.email({
        email: data.email,
        password: data.password,
        rememberMe: true,
      });

      if (result.error?.code) {
        if (result.error.code === "EMAIL_NOT_VERIFIED") {
          setError("email", { message: t(`validation.emailNotVerified`) });
        } else {
          setError("password", { message: t(`validation.incorrectPassword`) });
        }
        setIsLoading(false);
      } else {
        queryClient.clear();
        await apiUtils.context.person.fetch();
        setIsSuccess(true);
      }
    })();
  };

  if (isSuccess) return <Redirect href={"/"} />;

  return (
    <AuthBaseForm onSubmit={handleSubmit(onSubmit)}>
      <View width={"100%"} gap={"$5"}>
        <FormTitle>{t("form.title")}</FormTitle>
        <View width={"100%"} gap={"$2"}>
          <View width={"100%"} gap={"$4"}>
            <Controller
              control={control}
              name={"email"}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <InputWithLabel
                  data-id={"signin-email"}
                  variant="inline"
                  size="md"
                  isError={!!fieldState.error?.message}
                  error={fieldState.error?.message}
                  label={t("form.email.label")}
                  placeholder={t("form.email.placeholder")}
                  onChangeText={(text) => field.onChange(text)}
                  {...field}
                />
              )}
            />

            <Controller
              control={control}
              name={"password"}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <InputWithLabel
                  data-id={"signin-password"}
                  secureTextEntry
                  variant="inline"
                  size="md"
                  isMessage
                  isError={!!fieldState.error?.message}
                  error={fieldState.error?.message}
                  label={t("form.password.label")}
                  message={t("form.password.message")}
                  placeholder={t("form.password.placeholder")}
                  onChangeText={(text) => field.onChange(text)}
                  {...field}
                />
              )}
            />
          </View>

          <Text
            asChild
            cursor="pointer"
            fontFamily={"$body"}
            fontSize={"$3"}
            color={"$gray-primary-900"}
            hoverStyle={{ color: "$lightblue-800" }}
            textDecorationLine="underline"
          >
            <Link
              data-id={"signin-forgot-password"}
              href={"/auth/forgot-password"}
            >
              {t("form.forgotLink")}
            </Link>
          </Text>
        </View>
      </View>

      <View gap={"$5"} $sm={{ gap: "$4" }}>
        <OrSeparator text={t("form.orSeparator")} />
        <YStack gap={"$2"}>
          <GoogleButton>{t("form.googleButton")}</GoogleButton>
          <FacebookButton>{t("form.facebookButton")}</FacebookButton>
        </YStack>

        <Separator
          borderBottomWidth={"$0.2"}
          borderColor={"$gray-secondary-400"}
        />
        <Form.Trigger asChild>
          <Button
            data-id={"signin-submit"}
            variant="primary"
            size={sm ? "md" : "lg"}
            rounded="lg"
          >
            {isLoading
              ? t("form.actions.submit.loading")
              : t("form.actions.submit.label")}
          </Button>
        </Form.Trigger>
      </View>
    </AuthBaseForm>
  );
};
